/**
 * Nigerian States and Local Government Areas (LGAs).
 *
 * Uses @some19ice/nigeria-geo-core for the canonical 36 states + FCT
 * and all 774 LGAs. This file re-exports in a simpler shape used by
 * the AddressForm component.
 */
import { getStates, getLGAsByState } from '@some19ice/nigeria-geo-core';

export interface NigerianState {
  id: string;
  name: string;
}

export interface NigerianLGA {
  id: string;
  name: string;
  stateId: string;
}

// Build sorted list of states
const rawStates = getStates();
export const NIGERIAN_STATES: NigerianState[] = rawStates
  .map((s: any) => ({ id: s.id, name: s.name }))
  .sort((a: NigerianState, b: NigerianState) => a.name.localeCompare(b.name));

// Build a map of stateId -> sorted LGAs
const lgaMap: Record<string, NigerianLGA[]> = {};
for (const st of rawStates) {
  const lgas = getLGAsByState(st.id);
  lgaMap[st.id] = (lgas || [])
    .map((l: any) => ({ id: l.id, name: l.name, stateId: st.id }))
    .sort((a: NigerianLGA, b: NigerianLGA) => a.name.localeCompare(b.name));
}

export function getLGAsForState(stateId: string): NigerianLGA[] {
  return lgaMap[stateId] || [];
}

/** Lookup state id from state name (case-insensitive) */
export function findStateId(stateName: string): string | null {
  if (!stateName) return null;
  const lower = stateName.toLowerCase();
  const match = NIGERIAN_STATES.find(
    (s) => s.name.toLowerCase() === lower || s.id === lower,
  );
  return match?.id || null;
}
